<template>
	<AppCard :title="getString('terminals_card_title')" :button-text="getString('save')" @button-click="saveSettings">
		<template #body>
			<AppList :items="terminals">
				<template #dropdown-slot="{item}">
					<div class="row centered space-between pb-20">
						<p>{{getString('pos_name')}}</p>
						<AppTextInput
							:value="item.name"
							:placeholder="getString('pos_name')"
							@pressed-enter="item.callbackText"
							@focusout-input="(e, value)=>{item.callbackText(value)}"
						/>
					</div>
					<div class="row centered space-between pb-20">
						<p>{{getString("associate_kiosk")}}</p>
						<AppSelect
							:values="tablesOptionsNames"
							:selected-index="getTablesIndex(item.id)"
							@selected="i => changeTerminal(i, item)" />
					</div>
					<div class="row centered space-between pb-20">
						<p>{{getString("hidden_for_users")}}</p>
						<AppChecklist
							@change-checked="newIndexes => changingTerminalHiddenUsers(item.id, newIndexes)"
							:title="getString('select_users')"
							:values="hideUsersEmails"
							:selected-indexes="selectedHiddenUsersEmailsIndexes(item.id)"
						/>
					</div>
					<div class="row center flex-end">
						<AppButton
							class="mb-16"
							:text="getString('pos_tips')"
							outline small
							@click="showTerminalTipsSettings(item.id)"/>
					</div>
					<div class="row centered flex-end">
						<AppButton
							:text="getString('delete')"
							outline small
							@click="clickDeleteTerminal(item)"
						/>
					</div>
				</template>
				<template #new-terminal-slot="{item}">
					<div class="row centered space-between row-clickable new-terminal-row" @click="item.callback">
						<div class="new-terminal-title">
							<div class="icon-plus" v-html="getIcon('iconPlus')"></div>
							<p class="chevron-p">{{ item.id === 'new_terminal_id' ? getString('add_pos') : item.name }}</p>
						</div>
						<div class="chevron-container">
							<AppChevronIcon :direction="item.active ? 'up' : 'down'"/>
						</div>
					</div>
				</template>
				<template #new-terminal-dropdown-slot="{item}">
					<AppList :items="newTerminalFields">
						<template #input-slot="{item}">
							<div class="row centered space-between">
								<p>{{item.label}}</p>
								<AppTextInput
									:value="item.value || ''"
									:placeholder="item.label"
									@changing-text="item.callback"
									@focusout-input="(e, value)=>{item.callback(value)}"
								/>
							</div>
						</template>
					</AppList>
				</template>
			</AppList>
		</template>
	</AppCard>

	<AppBottomSheet :active="terminalIdForTips !== null" :forceWidth="bottomSheetWidth" @toggling-popup="toggling">
		<TerminalTipsSettings v-if="terminalIdForTips !== null" :terminal-id="terminalIdForTips" @terminal-updated="toggling(false)"/>
	</AppBottomSheet>
</template>

<script>
import AppList from "@/shared/components/AppList.vue";
import AppButton from "@/shared/components/AppButton.vue";
import AppChecklist from "@/shared/components/AppChecklist.vue";
import AppChevronIcon from "@/shared/components/AppChevronIcon.vue";
import AppSelect from "@/shared/components/AppSelect.vue";
import AppCard from "@/shared/components/AppCard.vue";
import AppTextInput from "@/shared/components/AppTextInput.vue";
import {useLanguageStore, useNotificationStore, useSvgStore} from "@/shared/stores";
import {useRoomsStore, useUserStore} from "@/console-new/stores";
import TerminalTipsSettings from "@/console-new/components/plugins/TerminalTipsSettings.vue";
import AppBottomSheet from "@/shared/components/AppBottomSheet.vue";
import WindowWidth from "@/console-new/mixins/WindowWidth";

export default {
	setup() {
		let languageStore = useLanguageStore();
		let notificationStore = useNotificationStore();
		let userStore = useUserStore();
		let roomsStore = useRoomsStore();
		const svgStore = useSvgStore();
		return {getString: languageStore.getString, showNotification: notificationStore.showNotification, userStore, roomsStore, getIcon: svgStore.get};
	},
	emits: ["save-settings","delete-terminal"],
	name: "PosListQromo",
	components: {AppBottomSheet, TerminalTipsSettings, AppTextInput, AppCard, AppSelect, AppChevronIcon, AppChecklist, AppButton, AppList},
	data(){
		return {
			activeTerminalId: null,
			newTerminalObj: {
				id: 'new_terminal_id',
				title: this.getString('new_terminal'),
				chevronItem: true,
				dropdownSlot: 'new-terminal-dropdown-slot',
			},
			newTerminalName: '',
			newTerminalCode: '',
			terminalIdForTips: null,
			tablesOptions: [],
		}
	},
	mixins: [WindowWidth],
	computed: {
		hideUsersEmails() {
			if(!this.userStore.businessUsers) {
				return [];
			}
			let finalUsersEmails = [];
			Object.values(this.userStore.businessUsers).forEach((user) => {
				if(user.email) { finalUsersEmails.push(user.email); }
			});
			return finalUsersEmails;
		},
		hideUsersIds() {
			if(!this.userStore.businessUsers) {
				return [];
			}
			let finalUsersIds = [];
			Object.values(this.userStore.businessUsers).forEach((user) => {
				if(user.user_id) { finalUsersIds.push(user.user_id); }
			});
			return finalUsersIds;
		},
		tablesOptionsNames() {
			let tables = this.tablesOptions.map((table) => table.table_name);
			let result = [this.getString('not_a_kiosk')];
			result = result.concat(tables);
			return result;
		},
		newTerminalFields() {
			return [
				{
					title: this.getString('pos_name'),
					label: this.getString('pos_name'),
					key: "name",
					customSlot: "input-slot",
					callback: (value) => { this.newTerminalName = value; },
					value: this.newTerminalName
				},
				{
					title: this.getString('new_pos_code'),
					label: this.getString('new_pos_code'),
					key: "code",
					customSlot: "input-slot",
					callback: (value) => {
						this.newTerminalCode = value.replace(/\s+/g, '-');
					},
					value: this.newTerminalCode
				},
				{
					title: this.getString('consult_guide'),
					buttonTitle: this.getString('label_button_help_desk'),
					callbackButton: this.openHelpDeskLink
				},
			];
		},
		newTerminal() {
			let returningTerminal = Object.assign({}, this.newTerminalObj);
			returningTerminal.name = this.newTerminalName;
			returningTerminal.code = this.newTerminalCode;
			returningTerminal.active = this.activeTerminalId === 'new_terminal_id';
			returningTerminal.customSlot = "new-terminal-slot";
			returningTerminal.callback = () => {
				this.activeTerminalId = this.activeTerminalId === 'new_terminal_id' ? null : 'new_terminal_id';
			};
			return returningTerminal;
		},
		terminals() {
			let terminals = this.userStore.businessTerminals || [];
			let finalTerminals = [];
			terminals.forEach((terminal) => {
				finalTerminals.push({
					id: terminal.terminal_id,
					title: terminal.name,
					name: terminal.name,
					callback: value => {
						this.activeTerminalId = value ? terminal.terminal_id : null;
					},
					callbackText: value => {
						let currentTerminal = this.userStore.businessTerminals.find(t => t.terminal_id === terminal.terminal_id);
						if(currentTerminal && value) {
							currentTerminal.name = value;
						}
					},
					chevronItem: true,
					dropdownSlot: 'dropdown-slot',
					active: this.activeTerminalId === terminal.terminal_id,
				});
			});
			finalTerminals.push(this.newTerminal);
			return finalTerminals;
		},
		bottomSheetWidth() {
			if(this.windowWidth < 550) {
				return "100%";
			} else if(this.windowWidth < 768) {
				return "550px";
			} else {
				return "720px";
			}
		},
	},
	methods: {
		clickDeleteTerminal(item) {
			if(TEST && item) {
				let currentTerminal = this.userStore.businessTerminals.find(t => t.terminal_id === item.id);
				if(currentTerminal && (currentTerminal?.reader_id === 'tmr_GMicWAr7dilMGZ' || currentTerminal.reader_id === 'tmr_GMicdgMbPvIYIy')) {
					let confirmDeletingDevTerminalPhrase = 'Attenzione, developer!\nCancellando questo terminal, costringerai tutti i tuoi colleghi a re-inizializzarlo.\nPer procedere comunque, scrivi il nome del POS che vuoi eliminare:';
					let typedName = prompt(confirmDeletingDevTerminalPhrase);
					if(!typedName || typedName !== currentTerminal.name) {
						return
					}
				}
			}
			this.$emit('delete-terminal', item)
		},
		toggling(open) {
			if(!open) {
				this.terminalIdForTips = null;
			}
		},
		saveSettings() {

			let name = this.newTerminalName;
			let code = this.newTerminalCode;
			if((name || code) && (!name || !code)){
				this.showNotification('incomplete-new-terminal-warning');
				return;
			}
			if(code && code.length < 6) {
				this.showNotification('short-pos-code');
				return;
			}
			let newTerminalData = name && code ? {
				name: name,
				code: code,
				terminal_id: 'new_terminal'
			} : null;

			this.$emit('save-settings', newTerminalData, () => {
				this.activeTerminalId = null;
				this.newTerminalName = '';
				this.newTerminalCode = '';
			});

		},
		updateTablesOptions() {
			this.roomsStore.getBusinessRooms(r => {
				if(r) {
					this.tablesOptions = this.roomsStore.tables;
				}
			});
		},
		changingTerminalHiddenUsers(terminalId, newIndexes) {
			if(!this.userStore?.changedHiddenTerminalsUsers) {
				this.userStore.changedHiddenTerminalsUsers = {};
			}
			this.userStore.changedHiddenTerminalsUsers[terminalId] = true;

			if(!this.userStore.businessHiddenTerminalsUsers) {
				this.userStore.businessHiddenTerminalsUsers = {};
			}
			if(newIndexes && newIndexes.length) {
				this.userStore.businessHiddenTerminalsUsers[terminalId] = newIndexes.map(index => this.hideUsersIds[index])
			} else {
				this.userStore.businessHiddenTerminalsUsers[terminalId] = [];
			}
		},
		selectedHiddenUsersEmailsIndexes(terminalId) {
			if(!this.userStore?.businessHiddenTerminalsUsers || !this.userStore.businessHiddenTerminalsUsers[terminalId]?.length) {
				return [];
			}
			let returningIndexes = [];
			this.userStore.businessHiddenTerminalsUsers[terminalId].forEach((userId) => {
				this.hideUsersIds.forEach((id, i) => {
					if (Number(id) === Number(userId)) {
						returningIndexes.push(i);
					}
				});
			});
			return returningIndexes;
		},
		changeTerminal(i, item) {
			this.tablesOptions.forEach((table) => {
				if(table.terminal_id === Number(item.id)) {
					table.terminal_id = null;
				}
			});
			if(i > 0) {
				let table = this.tablesOptions[i-1];
				const termId = item.id;
				this.userStore.businessTerminals.forEach((terminal) => {
					if(terminal.terminal_id === termId) {
						terminal.room_table_id = table.room_table_id;
					}
				});
			}
		},
		getTablesIndex(terminalId) {
			if(terminalId) {
				let tableI = this.tablesOptions.findIndex(function(t) { return t.terminal_id === Number(terminalId)});
				if(tableI > -1) {
					return tableI+1;
				}
			}
			return 0;
		},
		showTerminalTipsSettings(terminalId) {
			this.terminalIdForTips = parseInt(terminalId);
		},
		openHelpDeskLink() {
			window.open('https://qromo-help.freshdesk.com/support/solutions/articles/202000076884-configurazione-qromo-pos', '_blank');
		}
	},
	mounted() {
		this.updateTablesOptions();
	},
	watch: {
		'userStore.currBusinessId': {
			handler(newVal, oldVal) {
				if(newVal && newVal !== oldVal) {
					this.activeTerminalId = this.activeTerminalId === 'new_terminal_id' ? 'new_terminal_id' : null;
					this.updateTablesOptions();
				}
			},
		},
	},
}
</script>

<style scoped lang="scss">
@use "@/console-new/assets/scss/_variables.scss" as *;
.card-container:deep(.card-body) {
	padding: 0 !important;
	& > .list-container {
		.list-item:first-child::before {
			display: none;
		}
		.list-item:last-child::after {
			display: none;
		}
	}
}
.extra-text {
	width: 100%;
	cursor: pointer;
	p {
		font-size: 14px;
		line-height: 16px;
		color: #9B9B9B;
	}
}
.switch-header-wrapper {
	@include centered;
	justify-content: flex-end;
	@media (max-width: 350px) {
		width: 100%;
		margin-top: 10px;
	}
}
.chevron-p {
	width: calc(100% - 75px);
}
.chevron-p, .no-printers-row p {
	@media (max-width: 600px) {
		font-size: 14px;
	}
	@media (max-width: 400px) {
		font-size: 12px;
	}
}
.no-printers-row {
	@include centered;
	justify-content: flex-start;
	padding: 10px;
}
.row-clickable {
	cursor: pointer;
	img {
		display: none;
	}
	.chevron-container {
		@include centered;
		justify-content: flex-end;
		width: 50px;
		height: 35px;
	}
}
.new-terminal-row {
	min-height: 35px;
	cursor: pointer;
	background-color: var(--color-bg-primary-2);
	padding: 5px 10px;
	border-radius: 13px;
	.chevron-container {
		@include centered;
		justify-content: flex-end;
		width: 50px;
		height: 35px;
	}
	.new-terminal-title {
		display: flex;
		align-items: center;
		width: calc(100% - 50px);
		p {
			color: var(--color-primary);
		}
		.icon-plus {
			width: 14px;
			height: 14px;
			margin-right: 10px;
			display: flex;
			align-items: center;
			justify-content: center;

			&:deep(svg) {
				width: 100%;
				height: 100%;

				.fill {
					fill: var(--color-primary);
				}
			}
		}
	}
}
.card-margin {
	margin-top: $mainPadding;
}
</style>